import React from 'react';

import Icons from '../../components/Icons/Icons.js';
import css from './ListingRedesign.module.css';

/**
 * SectionStickyContactBar — the #1 mobile conversion lever (strategy doc).
 * Price + status stay pinned to the bottom of the viewport with Call / Text /
 * Message actions through the whole scroll, so the renter never hunts for
 * contact info. Mobile only (hidden >= viewportLarge via CSS).
 *
 * Appears after the user scrolls past the top contact card (~420px) so it does
 * not fight the above-the-fold contact card on first paint.
 *
 * All three actions reuse the SAME handlers/trackers as the main card, so taps
 * here are attributed identically (owner proof stats + Meta CAPI).
 *
 * @param {String}   priceText     e.g. "$120" (short); optional
 * @param {String}   telLink       formatted tel: href (optional)
 * @param {String}   smsLink       formatted sms: href (optional)
 * @param {Function} onContact     Send Message handler
 * @param {Function} onCallClick   tracker for call tap (optional)
 * @param {Function} onTextClick   tracker for text tap (optional)
 */
const SectionStickyContactBar = ({
  priceText,
  telLink,
  smsLink,
  onContact,
  onCallClick,
  onTextClick,
}) => {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onScroll = () => setVisible(window.scrollY > 420);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Flag the body while this bar is mounted so the global chat launcher can lift
  // itself above the bar on mobile (see LoadBot). Always cleaned up on unmount.
  React.useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.body.classList.add('has-sticky-contact-bar');
    return () => document.body.classList.remove('has-sticky-contact-bar');
  }, []);

  if (!visible) return null;

  // Nothing actionable at all -> don't render an empty bar.
  if (!telLink && !smsLink && typeof onContact !== 'function') return null;

  return (
    <div className={css.rdStickyBar} role="region" aria-label="Contact owner">
      {priceText ? (
        <div className={css.rdStickyInfo}>
          <span className={css.rdStickyPrice}>{priceText}</span>
          <span className={css.rdStickyStatus}>Available</span>
        </div>
      ) : null}

      <div className={css.rdStickyBtns}>
        {telLink ? (
          <a className={`${css.rdStickyBtn} ${css.rdStickyCall}`} href={telLink} onClick={onCallClick}>
            <Icons name="phoneFilled" /> Call
          </a>
        ) : null}
        {smsLink ? (
          <a className={`${css.rdStickyBtn} ${css.rdStickyText}`} href={smsLink} onClick={onTextClick}>
            <Icons name="message" /> Text
          </a>
        ) : null}
        {typeof onContact === 'function' ? (
          <button type="button" className={`${css.rdStickyBtn} ${css.rdStickyMsg}`} onClick={onContact}>
            <Icons name="envelope" /> Message
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default SectionStickyContactBar;
